import ModalConfirmacao2 from "components/ModalConfirmacao2/ModalConfirmacao2";
import { useProfile } from "contexts/ProfileContext";
import { useTabela } from "contexts/Tabela";
import { useToast } from "contexts/ToastContext";
import Button from "core/components/Button";
import Separator from "core/components/Separator";
import SelectInputField from "core/data-entry/SelectInputField";
import Modal from "core/feedback/Modal";
import { motion } from "framer-motion";
import useService from "hooks/useService";
import { Filial } from "models/Filial";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { mudarContextoEmbarque } from "services/ModuloGerenciador/Viagens";

interface ModalAlterarUnidadeEmbarqueProps {
    open: boolean;
    onClose: () => void;
}

function ModalAlterarUnidadeEmbarque({
    open,
    onClose,
}: ModalAlterarUnidadeEmbarqueProps) {
    const { setContextoEmbarque, dadosFilial, profile } = useProfile();
    const navigate = useNavigate();
    const { tabelaRedux } = useTabela();
    const { showToast } = useToast();

    const [, editarUnid] = useService(mudarContextoEmbarque,
        {
            autoStart: false,
            onData: () => {
                setContextoEmbarque(filialSelecionada?.contexto || "");
                navigate(`/viagens/${filialSelecionada?.contexto || ""}`);
                onClose();
            },
            onError: (error) => {
                if (typeof error.response.data === "string") {
                    showToast(error.response.data, "error");
                } else {
                    showToast(
                        "Ocorreu um erro inesperado, volte e tente novamente, caso o problema persista entre em contato com o suporte.",
                        "error"
                    );
                }
            },
        }
    );

    const filiaisOptions = useMemo(() => {
        if (!dadosFilial) return [];
        return dadosFilial
            .filter((filial: Filial) => !["templateEmbarque", "todos"].includes(filial.contexto))
            .sort((a: Filial, b: Filial) => {
                if (a.nomeFilial && b.nomeFilial) {
                    return a.nomeFilial.localeCompare(b.nomeFilial);
                }
                return 0;
            })
            .map((filial: Filial) => ({
                obj: filial.id,
                title: filial.nomeFilial || "",
            }));
    }, [dadosFilial]);

    const filialEmbarqueAtual = useMemo(() => {
        if (!dadosFilial) return null;
        return dadosFilial.find(
            (filial: Filial) => filial.nomeFilial === tabelaRedux?.row?.unidEmbarque
        );
    }, [dadosFilial, tabelaRedux?.row?.unidEmbarque]);

    const [selectedFilial, setSelectedFilial] = useState<{ obj: number, title: string } | null>(null);
    const [openConfirmacao, setOpenConfirmacao] = useState(false);

    const filialSelecionada = useMemo(() => {
        if (!filialEmbarqueAtual) return null;
        return dadosFilial?.find(
            (filial: Filial) => filial.nomeFilial === selectedFilial?.title
        );
    }, [dadosFilial, filialEmbarqueAtual, selectedFilial?.title]);

    const handleSave = useCallback(() => {
        setOpenConfirmacao(true);
    }, []);

    const handleConfirmar = useCallback(() => {
        if (selectedFilial) {
            editarUnid({
                token: profile?.token || "",
                unidEmbarque: selectedFilial.title,
                contexto: filialSelecionada?.contexto || "",
                idEmbarque: tabelaRedux?.row?.idEmbarque
            });
        }
    }, [editarUnid, profile?.token, selectedFilial, tabelaRedux?.row?.idEmbarque, filialSelecionada]);

    useEffect(() => {
        if (!open) {
            setSelectedFilial(null);
            return;
        }
        if (filialEmbarqueAtual) {
            setSelectedFilial({
                obj: filialEmbarqueAtual.id,
                title: filialEmbarqueAtual.nomeFilial || "",
            });
        }
    }, [open, filialEmbarqueAtual]);

    return (
        <Modal isOpen={open} onClose={onClose}>
            <motion.div
                onClick={(e) => e.stopPropagation()}
                initial={{ opacity: 0, y: 50 }}
                exit={{ opacity: 0, y: 50 }}
                animate={{
                    opacity: 1,
                    y: 0,
                }}
                transition={{ duration: 0.3 }}
                className="modal-editar-datas-container"
            >
                <div className="content-container modal-content">
                    <div>Alterar Unidade de Embarque:</div>
                    <Separator size="xl" />
                    <div className="select-filial-container">
                        <SelectInputField
                            label="Unidade de Embarque"
                            options={filiaisOptions}
                            value={selectedFilial?.title || ""}
                            onChange={(value) => (
                                setSelectedFilial(
                                    value
                                )
                            )}
                            placeholder="Selecione a unid embarque"
                        />
                    </div>
                </div>
                <div className="footer-container">
                    <Button
                        theme="primary"
                        label="Salvar"
                        onClick={handleSave}
                        disabled={!selectedFilial || selectedFilial?.title === filialEmbarqueAtual?.nomeFilial}
                        radius="md"
                    />
                    <Separator direction="horizontal" size="md" />
                    <Button
                        theme="secondary"
                        label="Cancelar"
                        onClick={onClose}
                        radius="md"
                    />
                </div>
                <ModalConfirmacao2
                    titulo="Tem certeza que deseja alterar a unidade de embarque?"
                    texto={`A unidade de embarque será alterada de ${filialEmbarqueAtual?.nomeFilial} para ${selectedFilial?.title}.`}
                    tituloBotao="Cancelar"
                    textoBotao="Alterar"
                    open={openConfirmacao}
                    onClose={() => setOpenConfirmacao(false)}
                    onClick={handleConfirmar}
                />
            </motion.div>
        </Modal>
    );
};

export default ModalAlterarUnidadeEmbarque;